/**
 * AgentPortal — top-level shell for the Agent role.
 *
 * Split into per-screen modules under `./agent/*` during the iter35 refactor.
 * This file is now ~180 lines: header, sidebar, polling, routing.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, ClipboardList, Users as UsersIcon, LogOut, ChevronRight,
  ShoppingCart, Menu, X, AlertCircle, Sparkles, FileText, Receipt, Video,
  FolderOpen, PanelLeftClose, PanelLeftOpen, Wallet, ExternalLink, Settings as SettingsIcon, Inbox, Tags, Truck,
} from "lucide-react";
import EmailSettingsCard from "../components/EmailSettingsCard";
import WebmailPage from "../components/WebmailPage";
import { ChatButton, ChatDrawer } from "../components/ChatDrawer";
import { AgentOffersList, OffersBadge } from "../components/OffersComponents";
import DedicatedOffersList from "../components/DedicatedOffersList";
import { Badge } from "../components/ui/badge";
import { useLang } from "../context/LangContext";
import StatementsPage from "../pages/StatementsPage";
import AgentMyCommissions from "../components/AgentMyCommissions";
import DocumentsPage from "../pages/DocumentsPage";
import TutorialsPage from "../pages/TutorialsPage";

import { API, Logo } from "./agent/_shared";
import Dashboard from "./agent/AgentDashboard";
import OrdersList from "./agent/AgentOrders";
import CustomersList from "./agent/AgentCustomers";
import NewOrderPage from "./agent/AgentNewOrder";
import ApprovalsList from "./agent/AgentApprovals";
import AgentPricelists from "./agent/AgentPricelists";
import LogisticsPage from "../pages/LogisticsPage";

const Sidebar = ({ user, logout, pendingApprovalCount = 0, mobileOpen, setMobileOpen, collapsed, onToggleCollapsed, disabledModules = [] }) => {
  const { t } = useLang();
  const loc = useLocation();
  const navigate = useNavigate();
  const items = [
    { path: "/agent/dashboard", label: t.dashboard, icon: LayoutDashboard },
    { id: "/agent/new-order", path: "/agent/new-order", label: t.pAgent_newOrder, icon: ShoppingCart },
    { path: "/agent/pricelists", label: "Listini", icon: Tags },
    { path: "/agent/offers", label: t.offers, icon: Sparkles },
    { path: "/agent/dedicated-offers", label: "Offerte Dedicate", icon: FileText },
    { path: "/agent/approvals", label: t.pendingApproval, icon: AlertCircle, badge: pendingApprovalCount },
    { path: "/agent/orders", label: t.orders, icon: ClipboardList },
    ...(user?.role === "admin" || user?.purchases_enabled ? [{ path: "/purchases/suppliers", label: "Acquisti", icon: ShoppingCart }] : []),
    { path: "/agent/documents", label: "Documenti", icon: FolderOpen },
    { path: "/agent/customers", label: t.customers, icon: UsersIcon },
    { path: "/agent/logistics", label: "Logistica", icon: Truck },
    { path: "/agent/statements", label: "Scadenziario", icon: Receipt },
    { path: "/agent/commissions", label: "Le mie provvigioni", icon: Wallet },
    { path: "/agent/tutorials", label: "Guide", icon: Video },
    { path: "/agent/mail", label: "Mail", icon: Inbox },
    { path: "/agent/settings", label: "Impostazioni", icon: SettingsIcon },
  ].filter((it) => !disabledModules.includes(it.path.split("/").pop()));
  const body = (
    <>
      <div className="p-6 border-b border-stone-200 flex items-center justify-between">
        <Logo className="h-10" />
        <button className="hidden lg:flex p-1 text-stone-400 hover:text-stone-700" onClick={onToggleCollapsed} title="Comprimi menu" data-testid="agent-sidebar-collapse"><PanelLeftClose className="w-5 h-5" /></button>
        <button className="lg:hidden p-1 text-stone-500" onClick={() => setMobileOpen(false)} data-testid="agent-sidebar-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="px-6 py-3 border-b border-stone-200">
        <p className="text-xs text-stone-500">{user?.role === "admin" ? `${t.admin} · ${t.agent}` : `${t.agent}`}</p>
        <p className="font-semibold text-stone-900 truncate">{user?.name}</p>
      </div>
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {items.map((it) => {
          const active = loc.pathname.startsWith(it.path);
          return (
            <Link key={it.path} to={it.path} onClick={() => setMobileOpen(false)} className={`group flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${active ? "bg-[#8A1C2A] text-white shadow-sm" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`agent-nav-${it.path.split("/").pop()}`}>
              <it.icon className="w-5 h-5" />
              <span className="flex-1">{it.label}</span>
              {it.badge > 0 && <Badge className="bg-amber-500 text-white text-xs">{it.badge}</Badge>}
              <span
                role="button"
                title="Apri in un'altra scheda"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(it.path, "_blank"); }}
                className={`p-1 rounded opacity-50 lg:opacity-0 lg:group-hover:opacity-60 hover:!opacity-100 transition-opacity ${active ? "hover:bg-white/20" : "hover:bg-stone-200"}`}
                data-testid={`agent-newtab-${it.path.split("/").pop()}`}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-stone-200 space-y-1 pb-[max(1rem,env(safe-area-inset-bottom))] shrink-0">
        {user?.role === "admin" && (
          <button onClick={() => { setMobileOpen(false); navigate("/admin"); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#8A1C2A] font-semibold hover:bg-[#8A1C2A]/10 rounded-md" data-testid="agent-back-to-admin-btn">
            <ChevronRight className="w-4 h-4 rotate-180" />Torna ad Admin
          </button>
        )}
        <button onClick={() => { logout(); navigate(user?.role === "admin" ? "/login" : "/login/agent"); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-md" data-testid="agent-logout-btn">
          <LogOut className="w-4 h-4" />Esci
        </button>
      </div>
    </>
  );
  const compactBody = (
    <>
      <div className="p-3 border-b border-stone-200 flex justify-center shrink-0">
        <button onClick={onToggleCollapsed} className="p-2 rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-100" title="Espandi menu" data-testid="agent-sidebar-expand"><PanelLeftOpen className="w-5 h-5" /></button>
      </div>
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
        {items.map((it) => {
          const active = loc.pathname.startsWith(it.path);
          return (
            <Link key={it.path} to={it.path} title={it.label} className={`relative flex items-center justify-center p-2.5 rounded-lg transition-all ${active ? "bg-[#8A1C2A] text-white shadow-sm" : "text-stone-600 hover:bg-stone-100"}`} data-testid={`agent-nav-collapsed-${it.path.split("/").pop()}`}>
              <it.icon className="w-5 h-5" />
              {it.badge > 0 && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">{it.badge}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="p-2 border-t border-stone-200 flex flex-col items-center gap-1 pb-[max(0.5rem,env(safe-area-inset-bottom))] shrink-0">
        {user?.role === "admin" && (
          <button onClick={() => navigate("/admin")} className="p-2 text-[#8A1C2A] hover:bg-[#8A1C2A]/10 rounded-md" title="Torna ad Admin" data-testid="agent-back-to-admin-collapsed">
            <ChevronRight className="w-4 h-4 rotate-180" />
          </button>
        )}
        <button onClick={() => { logout(); navigate(user?.role === "admin" ? "/login" : "/login/agent"); }} className="p-2 text-stone-500 hover:bg-stone-100 rounded-md" title="Esci" data-testid="agent-logout-collapsed"><LogOut className="w-4 h-4" /></button>
      </div>
    </>
  );
  return (
    <>
      <aside className={`hidden lg:flex fixed left-0 top-0 bottom-0 ${collapsed ? "w-[64px]" : "w-64"} bg-white border-r border-stone-200 flex-col z-30 transition-[width] duration-200`}>
        {collapsed ? compactBody : body}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white border-r border-stone-200 flex flex-col">{body}</aside>
        </div>
      )}
    </>
  );
};

const MobileHeader = ({ onMenuClick, onChatClick }) => {
  const navigate = useNavigate();
  return (
    <div className="lg:hidden sticky top-0 z-20 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
      <button onClick={onMenuClick} className="p-2 -ml-2 text-stone-700" data-testid="agent-mobile-menu-btn">
        <Menu className="w-5 h-5" />
      </button>
      <Logo className="h-8" />
      <div className="flex items-center">
        <OffersBadge onClick={() => navigate("/agent/offers")} data-testid="offers-badge-btn-mobile" />
        <ChatButton onClick={onChatClick} data-testid="chat-open-btn-mobile" />
      </div>
    </div>
  );
};

const DesktopHeader = ({ onChatClick }) => {
  const navigate = useNavigate();
  return (
    <div className="hidden lg:flex sticky top-0 z-20 bg-white border-b border-stone-200 px-6 py-2 items-center justify-end gap-1">
      <OffersBadge onClick={() => navigate("/agent/offers")} data-testid="offers-badge-btn-desktop" />
      <ChatButton onClick={onChatClick} data-testid="chat-open-btn-desktop" />
    </div>
  );
};

const AGENT_MODULE_ORDER = ["dashboard", "new-order", "pricelists", "offers", "dedicated-offers", "approvals", "orders", "documents", "customers", "logistics", "statements", "commissions", "tutorials"];

const AgentPortal = ({ user, logout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [disabledModules, setDisabledModules] = useState(user?.role === "agent" ? null : []);
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("giotto_agent_sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("giotto_agent_sidebar_collapsed", c ? "0" : "1"); return !c; });
  useEffect(() => {
    if (user?.role !== "agent") return;
    axios.get(`${API}/my-modules`)
      .then((r) => setDisabledModules(r.data?.disabled_modules || []))
      .catch(() => setDisabledModules([]));
  }, [user]);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/orders?status=pending_agent`);
        if (!cancelled) setPendingCount((r.data || []).length);
      } catch (e) { console.debug(e); }
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  // refreshPending is exposed so child screens can trigger an immediate refresh
  // (e.g. after approving/rejecting an order). Wrapped in useCallback for stable ref.
  const refreshPending = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/orders?status=pending_agent`);
      setPendingCount((r.data || []).length);
    } catch (e) { console.debug(e); }
  }, []);
  return (
    <div className="min-h-screen bg-stone-50">
      {/* Modulo disattivato dall'admin → reindirizza al primo modulo consentito */}
      {Array.isArray(disabledModules) && disabledModules.includes(loc.pathname.split("/")[2] || "") && (
        <Navigate to={`/agent/${AGENT_MODULE_ORDER.find((k) => !disabledModules.includes(k)) || "dashboard"}`} replace />
      )}
      <Sidebar user={user} logout={logout} pendingApprovalCount={pendingCount} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} disabledModules={disabledModules || []} />
      <div className={collapsed ? "lg:pl-[64px]" : "lg:pl-64"}>
        <MobileHeader onMenuClick={() => setMobileOpen(true)} onChatClick={() => setChatOpen(true)} />
        <DesktopHeader onChatClick={() => setChatOpen(true)} />
        <Routes>
          <Route path="/dashboard" element={<Dashboard onPendingChanged={refreshPending} />} />
          <Route path="/new-order" element={<NewOrderPage />} />
          <Route path="/pricelists" element={<AgentPricelists />} />
          <Route path="/offers" element={<div className="p-4 sm:p-6 lg:p-8"><AgentOffersList /></div>} />
          <Route path="/dedicated-offers" element={<div className="p-4 sm:p-6 lg:p-8"><DedicatedOffersList /></div>} />
          <Route path="/approvals" element={<ApprovalsList onChanged={refreshPending} />} />
          <Route path="/orders" element={<OrdersList />} />
          <Route path="/documents" element={<div className="p-4 sm:p-6 lg:p-8"><DocumentsPage currentUser={user} company={null} /></div>} />
          <Route path="/customers" element={<CustomersList />} />
          <Route path="/logistics" element={<div className="p-4 sm:p-6 lg:p-8"><LogisticsPage canManage={false} /></div>} />
          <Route path="/statements" element={<div className="p-4 sm:p-6 lg:p-8"><StatementsPage currentUser={user} company={null} /></div>} />
          <Route path="/commissions" element={<div className="p-4 sm:p-6 lg:p-8"><AgentMyCommissions company={null} /></div>} />
          <Route path="/tutorials" element={<div className="p-4 sm:p-6 lg:p-8"><TutorialsPage currentUser={user} /></div>} />
          <Route path="/settings" element={<div className="p-4 sm:p-6 lg:p-8"><div className="max-w-3xl"><EmailSettingsCard /></div></div>} />
          <Route path="/mail" element={<div className="p-4 sm:p-6 lg:p-8"><WebmailPage /></div>} />
          <Route path="*" element={<Navigate to="/agent/dashboard" />} />
        </Routes>
      </div>
      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
};

export default AgentPortal;
